import type { ButtonHTMLAttributes, MouseEvent } from "react";

// Keep a focused button focusable while it is "busy" (awaiting a
// server round-trip) or otherwise momentarily unavailable.
//
// Why this exists: a plain `<button disabled>` is removed from the
// browser's focus ring and any keyboard user mid-tab loses their place.
// `aria-disabled` instead tells assistive tech the action is
// unavailable without confiscating focus, and the click-guard drops
// any presses while the flag is true.
//
// Usage:
//   <button
//     type="button"
//     onClick={inertOnClick(busy, onConfirm)}
//     {...inertAttrs(busy)}
//     {...primaryBtn(theme, busy)}
//   >Confirm</button>
//
// The visual disabled state (opacity / cursor) keeps coming from the
// existing button helpers (`primaryBtn(theme, busy)`); only the focus
// and clickability change.

type AnyHandler = (e?: MouseEvent<HTMLButtonElement>) => unknown;

export function inertOnClick<T extends AnyHandler | undefined>(
  inert: boolean,
  handler: T,
): T {
  if (!handler) return handler;
  // We must return a *fresh* function so React rebinds on toggle, but
  // the runtime behaviour just guards on `inert`.
  return ((e?: MouseEvent<HTMLButtonElement>) => {
    if (inert) {
      e?.preventDefault();
      return;
    }
    return handler(e);
  }) as T;
}

export function inertAttrs(
  inert: boolean,
): Pick<ButtonHTMLAttributes<HTMLButtonElement>, "aria-disabled"> {
  return {
    "aria-disabled": inert || undefined,
  };
}
